/**
 * LANDINGPAGE.JS | Micheals Infrastructure Advisory
 * COMPONENT: Principal Interface (Consultant Layman Rebrand)
 * VERSION: 2026.CORE.V5.CLEAN.HERO.DUAL_LOGIC
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import toast, { Toaster } from 'react-hot-toast';
import { ShieldCheck, Activity, ArrowRight, ChevronRight, X, Compass, Microscope, Cpu, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import InteractiveBooking from './InteractiveBooking';

const LandingPage = () => {
  const [email, setEmail] = useState('');
  const [showGateway, setShowGateway] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleInitiateProtocol = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) {
      toast.error('Valid Professional Email Required');
      return;
    }
    
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setShowGateway(true);
      toast.success('Secure Gateway Allocated');
    }, 1200);
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-[#f8f8f7] text-[#1c1917] min-h-screen selection:bg-stone-200 selection:text-stone-900 font-sans overflow-x-hidden"
    >
      <Toaster position="bottom-right" />
      <Helmet>
        <title>Independent Project Oversight & Advisory | Micheals Infrastructure Advisory</title>
      </Helmet>

      {/* --- SECURE GATEWAY MODAL --- */}
      <AnimatePresence>
        {showGateway && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              onClick={() => setShowGateway(false)}
              className="absolute inset-0 bg-stone-950/95 backdrop-blur-xl"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-2xl"
            >
              <button 
                onClick={() => setShowGateway(false)}
                className="absolute -top-12 right-0 text-white/40 hover:text-white flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold transition-all"
              >
                <X size={16} /> Terminate Session
              </button>
              
              <InteractiveBooking 
                leadEmail={email} 
                sessionId={`SES-${Date.now()}`}
                initialService="National_Advisory_Request"
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* --- HERO SECTION: COMPACT ACTION --- */}
      <section className="relative pt-48 pb-32 px-6 md:px-24 border-b border-stone-200 bg-[#f8f8f7]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
               <form onSubmit={handleInitiateProtocol} className="flex flex-col md:flex-row items-end gap-8 group">
                  <div className="flex-grow w-full md:max-w-md">
                    <p className="text-[10px] uppercase tracking-[0.5em] text-stone-400 mb-6 font-bold">Client Access</p>
                    <input 
                        type="email" required placeholder="OFFICE@YOURDOMAIN.COM"
                        className="w-full px-0 py-5 bg-transparent border-b border-stone-300 text-stone-900 focus:border-stone-950 outline-none transition-all placeholder:text-stone-300 font-mono text-[12px] uppercase tracking-[0.2em]"
                        value={email} onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <button type="submit" disabled={loading} className="whitespace-nowrap flex items-center gap-6 bg-stone-950 text-white px-12 py-6 uppercase text-[10px] tracking-[0.4em] font-bold hover:bg-stone-800 transition-all active:scale-95 shadow-lg">
                    {loading ? 'Authenticating...' : 'Request Consultation'}
                    <ChevronRight size={18} />
                  </button>
               </form>
            </motion.div>
          </div>
        </div>

        <div className="absolute top-48 right-12 text-stone-950 text-[9px] uppercase tracking-[0.6em] font-mono opacity-20 vertical-text hidden xl:block">
          MED_CONSULTING_SOLUTIONS // CORE_LOGIC_2026
        </div>
      </section>

      {/* --- DUAL-LOGIC VIEWPORT --- */}
      <section className="py-32 px-6 md:px-24 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <span className="text-[10px] font-black tracking-[0.5em] text-amber-600 mb-4 uppercase block">Logic // Aesthetic Integration</span>
            <h2 className="text-4xl md:text-5xl font-serif italic text-stone-950 max-w-2xl leading-tight">Forensic Infrastructure Intelligence<br/><span className="not-italic font-light text-stone-400">Strategic Capacity Oversight.</span></h2>
          </div>

          <div className="flex flex-col lg:flex-row gap-6 items-stretch">
            <motion.div {...fadeInUp} className="relative flex-1 aspect-[4/5] overflow-hidden rounded-sm shadow-2xl group">
              <img 
                src="/assets/table.webp" 
                alt="Client Interior Vision"
                className="w-full h-full object-cover grayscale brightness-90 contrast-110 group-hover:scale-105 transition-transform duration-1000"
              />
              <div className="absolute top-8 left-8 bg-white/10 backdrop-blur-md px-4 py-2 border border-white/20 text-white text-[9px] uppercase tracking-widest font-bold">
                Aesthetic Vision
              </div>
            </motion.div>

            <motion.div 
              {...fadeInUp} 
              transition={{ delay: 0.2 }}
              className="relative flex-1 aspect-[4/5] overflow-hidden rounded-sm shadow-2xl bg-stone-900 group"
            >
              <img 
                src="/assets/blueprint.webp" 
                alt="MED Technical Blueprint"
                className="w-full h-full object-cover opacity-70 grayscale brightness-110 contrast-125 group-hover:scale-105 transition-transform duration-1000"
              />
              <motion.div 
                animate={{ y: [0, 600, 0] }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                className="absolute top-0 left-0 w-full h-[1px] bg-amber-500/40 blur-[2px] pointer-events-none"
              />
              <div className="absolute top-8 right-8 bg-amber-600 px-4 py-2 text-white text-[9px] uppercase tracking-widest font-bold shadow-lg">
                Technical DNA
              </div>
            </motion.div>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-12 border-t border-stone-100 pt-16">
            {[
              { icon: Microscope, title: "Risk Mitigation", text: "Forensic analysis of infrastructure blueprints to eleminate systemic failure points before capital deployment." },
              { icon: Target, title: "Capacity Strategy", text: "Aligning energy architecture with projected industrial scaling requirements to ensure future-proof reliablitiy." },
              { icon: Cpu, title: "Infustructure Intelligence", text: "Unified digital and physical oversight " }
            ].map((item, i) => (
              <div key={i} className="space-y-4">
                <item.icon className="text-amber-600" size={20} />
                <h4 className="text-stone-950 font-bold text-[11px] uppercase tracking-widest">{item.title}</h4>
                <p className="text-stone-500 text-sm font-light leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- STRATEGIC PILLARS --- */}
      <section className="py-48 px-6 md:px-24 bg-[#f8f8f7]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-px bg-stone-100 border border-stone-100 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.1)] overflow-hidden rounded-[3rem]">
            
            <motion.div 
              whileInView={{ opacity: 1 }} initial={{ opacity: 0 }} viewport={{ once: true }}
              onClick={() => navigate('/design-specs')}
              className="md:col-span-2 bg-white p-12 md:p-24 flex flex-col justify-between hover:bg-stone-50 transition-colors cursor-pointer group"
            >
              <div>
                <span className="text-stone-100 group-hover:text-stone-200 transition-colors mb-12 text-9xl font-serif italic block select-none">01</span>
                <h3 className="text-stone-950 text-5xl font-serif mb-10 leading-tight">Expert <br/>Architectural Certainty</h3>
                <p className="text-stone-500 text-xl font-light leading-relaxed max-w-xl mb-12">
                  We establish the technical foundation for high-capital ventures. By pre-validating system performance, we eliminate systemic failure risks before capital deployment.
                </p>
              </div>
              <div className="flex items-center gap-6 text-stone-950 font-bold text-[11px] uppercase tracking-[0.5em]">
                Explore Services <ArrowRight size={20} className="group-hover:translate-x-3 transition-transform" />
              </div>
            </motion.div>

            <div className="flex flex-col divide-y divide-stone-100">
              <motion.div 
                whileInView={{ opacity: 1 }} initial={{ opacity: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
                className="bg-white p-14 flex-1 flex flex-col items-start justify-center"
              >
                <div className="p-5 bg-stone-50 rounded-2xl mb-10 text-stone-800"><ShieldCheck size={36} strokeWidth={1.5} /></div>
                <h3 className="text-2xl font-serif italic mb-6 text-stone-950">Forensic Oversight</h3>
                <p className="text-stone-500 text-[15px] font-light leading-relaxed mb-8">
                  We provide independent technical verification to ensure total infrastructure compliance and performance-tier adherence.
                </p>
                <p className="font-mono text-[9px] uppercase tracking-[0.4em] text-stone-400 font-bold">Project Protection</p>
              </motion.div>

              <motion.div 
                whileInView={{ opacity: 1 }} initial={{ opacity: 0 }} viewport={{ once: true }} transition={{ delay: 0.4 }}
                className="bg-stone-950 p-14 flex-1 flex flex-col items-start justify-center text-white relative overflow-hidden"
              >
                <div className="absolute inset-0 opacity-[0.03] blueprint-grid pointer-events-none" />
                <div className="p-5 bg-white/5 rounded-2xl mb-10 text-white"><Compass size={36} strokeWidth={1.5} /></div>
                <h3 className="text-2xl font-serif italic mb-6 relative z-10">Asset Intelligence</h3>
                <p className="text-stone-400 text-[15px] font-light leading-relaxed mb-8 relative z-10">
                  The definitive intelligence layer for complex infrastructure. We ensure technical resiliency and maximize the long-term ROI of mission-critical assets.
                </p>
                <div className="flex items-center gap-3 relative z-10">
                    <Activity size={14} className="text-stone-500 animate-pulse" />
                    <p className="font-mono text-[9px] uppercase tracking-[0.4em] text-stone-500 font-bold">Independent Oversight</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="py-24 px-6 md:px-24 border-t border-stone-200 text-stone-400 bg-[#f8f8f7]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-16">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.6em] text-stone-950 mb-6 font-black">Micheals Infrastructure Advisory &copy; 2026</p>
            <p className="text-[10px] uppercase tracking-[0.2em] max-w-sm leading-loose opacity-60">
              Defining the standard for infrastructure technical integrity. Micheals Infrastructure Advisory maintains strict independence, providing forensic oversight exclusively to protect capital investment and asset performance.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-12 gap-y-6 font-mono text-[10px] uppercase tracking-[0.4em] font-black text-stone-950">
              <span className="cursor-pointer hover:text-stone-500 transition-colors">Forensic Audit</span>
              <span className="cursor-pointer hover:text-stone-500 transition-colors">Architectural Certainty</span>
              <span className="cursor-pointer hover:text-stone-500 transition-colors">Asset Intelligence</span>
          </div>
        </div>
      </footer>
    </motion.div>
  );
};

export default LandingPage;